// Employer-funded value: statutory employer contributions + employer-paid
// benefit lines from the payslip's EMPLOYER_CONTRIBUTION category (names come
// from the payroll engine's country packs — nothing country-specific here).
// Kept strictly separate from earnings and clearly labeled as not deducted.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/features/shared/components/ui/tooltip';
import { Skeleton } from '@/features/shared/components/ui/skeleton';
import type { PayslipDetail, YtdSummary } from '../types';
import { formatMoney, MASK } from '../lib/format';

interface EmployerContributionsProps {
  detail?: PayslipDetail;
  ytd?: YtdSummary;
  loading: boolean;
  revealed: boolean;
}

export const EmployerContributions: React.FC<EmployerContributionsProps> = ({ detail, ytd, loading, revealed }) => {
  const { t } = useTranslation('payroll');

  if (loading) return <Skeleton className="h-32 w-full rounded-xl" />;
  const group = detail?.categories.find((g) => g.type === 'EMPLOYER_CONTRIBUTION');
  const items = (group?.items ?? []).filter((i) => i.amount !== 0);
  if (!detail || items.length === 0) return null; // nothing employer-funded this period

  const money = (n: number) => (revealed ? formatMoney(n, detail.currency) : MASK);
  const ytdItems = new Map(
    (ytd?.categories.find((g) => g.type === 'EMPLOYER_CONTRIBUTION')?.items ?? []).map((i) => [i.code, i.amount]),
  );

  return (
    <TooltipProvider delayDuration={150}>
      <section aria-labelledby="mypay-employer">
        <h2 id="mypay-employer" className="mb-1 flex items-center gap-2 text-h3 text-charcoal">
          <Building2 className="h-5 w-5 text-[#D9711E]" /> {t('myPay.employerSectionTitle')}
        </h2>
        <p className="mb-3 text-xs text-muted-foreground">{t('myPay.employerValueNote')}</p>

        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="px-4 py-2 font-medium">{t('myPay.contribution')}</th>
                <th className="px-4 py-2 text-right font-medium">{detail.periodLabel}</th>
                {ytd && <th className="px-4 py-2 text-right font-medium">{t('myPay.ytdShort', { year: ytd.year })}</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.code} className="border-b border-border last:border-b-0">
                  <td className="px-4 py-2">
                    <span className="flex items-center gap-1.5 text-charcoal">
                      {it.label}
                      {it.explanation && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button type="button" className="text-muted-foreground/70 hover:text-charcoal" aria-label={it.explanation}>
                              <Info className="h-3.5 w-3.5" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs text-xs">{it.explanation}</TooltipContent>
                        </Tooltip>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-charcoal">{money(it.amount)}</td>
                  {ytd && (
                    <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
                      {ytdItems.has(it.code) ? money(ytdItems.get(it.code)!) : t('myPay.notAvailable')}
                    </td>
                  )}
                </tr>
              ))}
              <tr className="bg-muted/40">
                <td className="px-4 py-2 font-semibold text-charcoal">{t('myPay.total')}</td>
                <td className="px-4 py-2 text-right font-semibold tabular-nums text-charcoal">
                  {money(detail.totalEmployerContributions)}
                </td>
                {ytd && (
                  <td className="px-4 py-2 text-right font-semibold tabular-nums text-charcoal">
                    {money(ytd.totalEmployerContributions)}
                  </td>
                )}
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </TooltipProvider>
  );
};
